import React, { useState } from 'react';
import { WifiOff, ChevronUp, ChevronDown, Inbox } from 'lucide-react';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';

/**
 * OfflineBanner
 * --------------
 * Full-width amber banner that appears at the top of the app whenever the
 * device is OFFLINE. Reassures the provider that drafts auto-save and queue
 * for generation when back online, and explains what's temporarily disabled
 * (dictation, new note generation).
 *
 * Also shows a small badge of how many drafts are currently queued so the
 * provider has confidence nothing is being lost.
 *
 * Renders nothing when online — collapses to zero footprint.
 */
const OfflineBanner = () => {
  const { status } = useConnectionStatus();
  const { queueCount } = useOfflineQueue();
  const [expanded, setExpanded] = useState(false);

  if (status !== 'offline') return null;

  return (
    <div
      data-testid="offline-banner"
      className="w-full bg-amber-500/15 border-b border-amber-500/40 text-amber-100 px-4 py-2 backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <WifiOff className="w-4 h-4 flex-shrink-0 text-amber-300" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-200">
            Offline mode — your work is safe.
          </p>
          {!expanded && (
            <p className="text-xs text-amber-100/80 truncate">
              Drafts auto-save and queue for generation when you're back online.
              {queueCount > 0 && (
                <>
                  {' '}
                  <span className="font-semibold" data-testid="offline-queue-count">
                    {queueCount} draft{queueCount !== 1 ? 's' : ''} queued
                  </span>
                  .
                </>
              )}
            </p>
          )}
          {expanded && (
            <div className="text-xs text-amber-100/80 mt-1 space-y-1">
              <p>
                <strong className="text-amber-200">What still works:</strong>{' '}
                Read &amp; copy your last 50 notes • Save draft + Next Patient •
                Edit demographics, ICD-10s, ROS/PE selections.
              </p>
              <p>
                <strong className="text-amber-200">Temporarily paused:</strong>{' '}
                Voice dictation (needs network) and new note generation. Your
                Save &amp; Next snapshots will auto-generate once the
                connection returns.
              </p>
              {queueCount > 0 && (
                <p className="flex items-center gap-1.5 pt-1">
                  <Inbox className="w-3.5 h-3.5 text-amber-300" />
                  <span className="font-semibold text-amber-200" data-testid="offline-queue-count-expanded">
                    {queueCount} draft{queueCount !== 1 ? 's' : ''} queued for sync
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="text-amber-200 hover:text-amber-100 px-2 py-1 rounded text-xs inline-flex items-center gap-1 border border-amber-500/40 hover:border-amber-400/60 transition-colors"
          onClick={() => setExpanded((v) => !v)}
          data-testid="offline-banner-toggle"
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              Less <ChevronUp className="w-3 h-3" />
            </>
          ) : (
            <>
              Details <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OfflineBanner;
